import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { addressLookupApi, type AddressSuggestion, type ResolvedAddress } from "@/api/addressLookup";
import { createRandomId } from "@/shared/lib/createRandomId";

const DEBOUNCE_MS = 350;

const createSessionToken = () => createRandomId();

const getLookupError = (error: unknown, fallback: string) =>
	axios.isAxiosError(error) && typeof error.response?.data?.message === "string"
		? error.response.data.message
		: fallback;

export const useAddressAutocomplete = () => {
	const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const timerRef = useRef<number>();
	const requestRef = useRef<AbortController>();
	const sessionTokenRef = useRef(createSessionToken());
	const searchStartedRef = useRef(false);

	const cancelPending = useCallback(() => {
		window.clearTimeout(timerRef.current);
		requestRef.current?.abort();
	}, []);

	const clear = useCallback(() => {
		cancelPending();
		setSuggestions([]);
		setLoading(false);
		setError("");
		searchStartedRef.current = false;
	}, [cancelPending]);

	const search = useCallback(
		(input: string, countryCode: string) => {
			cancelPending();
			setError("");
			if (input.trim().length < 3 || !countryCode) {
				setSuggestions([]);
				setLoading(false);
				searchStartedRef.current = false;
				return;
			}

			if (!searchStartedRef.current) {
				searchStartedRef.current = true;
				setLoading(true);
			}
			timerRef.current = window.setTimeout(async () => {
				const controller = new AbortController();
				requestRef.current = controller;
				try {
					const result = await addressLookupApi.autocomplete(
						input,
						countryCode,
						sessionTokenRef.current,
						controller.signal,
					);
					setSuggestions(result);
				} catch (requestError: unknown) {
					if (!axios.isCancel(requestError)) {
						setError(getLookupError(requestError, "Unable to look up addresses."));
					}
				} finally {
					if (!controller.signal.aborted) setLoading(false);
				}
			}, DEBOUNCE_MS);
		},
		[cancelPending],
	);

	const select = useCallback(
		async (placeId: string): Promise<ResolvedAddress> => {
			clear();
			setLoading(true);
			try {
				const address = await addressLookupApi.resolve(placeId, sessionTokenRef.current);
				sessionTokenRef.current = createSessionToken();
				setSuggestions([]);
				return address;
			} catch (requestError: unknown) {
				const message = getLookupError(requestError, "Unable to load that address.");
				setError(message);
				throw requestError;
			} finally {
				setLoading(false);
			}
		},
		[clear],
	);

	useEffect(() => clear, [clear]);

	return { suggestions, loading, error, search, select, clear };
};
